import React, { useRef, useEffect } from 'react';

interface PinInputProps {
  value: string;
  index: number;
  onChange: (index: number, value: string) => void;
  onKeyDown: (index: number, key: string) => void;
  onPaste?: (index: number, value: string) => void;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
}

const PinInput: React.FC<PinInputProps> = ({
  value,
  index,
  onChange,
  onKeyDown,
  onPaste,
  disabled = false,
  error = false,
  autoFocus = false
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const pasteInProgressRef = useRef(false);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Cleanup paste flag on unmount
  useEffect(() => {
    return () => {
      pasteInProgressRef.current = false;
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // During a multi-digit paste the browser will briefly set the first input's value
    // to the full pasted string before we distribute it. Allow that transiently so
    // screen readers / native IME don't glitch, but do not emit multiple onChange calls.
    if (pasteInProgressRef.current) {
      return; // parent distribution via onPaste handles state
    }
    const newValue = e.target.value;
    if (newValue.length <= 1 && /^\d*$/.test(newValue)) {
      onChange(index, newValue);
    } else if (newValue.length > 1) {
      // Defensive: if user somehow pastes while paste flag not yet set, route through handlePaste logic
      const digits = newValue.replace(/\D/g, '');
      if (digits.length > 1 && onPaste) {
        onPaste(index, digits);
      } else if (digits.length === 1) {
        onChange(index, digits);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown(index, e.key);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Set flag to prevent handleChange from interfering
    pasteInProgressRef.current = true;
    
    // Prefer nullish coalescing over logical OR to avoid treating empty string as falsy fallback
    const raw = e.clipboardData.getData('text') ?? e.clipboardData.getData('text/plain');
    if (!raw) {
      pasteInProgressRef.current = false;
      return;
    }
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      pasteInProgressRef.current = false;
      return;
    }
    e.preventDefault();
    
    if (digits.length <= 1) {
      onChange(index, digits);
      // Reset flag immediately for single digit paste
      pasteInProgressRef.current = false;
      return;
    }
    if (onPaste) {
      onPaste(index, digits);
    } else {
      onChange(index, digits[0]);
    }
    // Clear paste flag on next animation frame so tests can explicitly flush and
    // we ensure parent distribution state has committed before allowing input changes
    requestAnimationFrame(() => { pasteInProgressRef.current = false; });
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      disabled={disabled}
      className={`
        w-8 h-8 sm:w-12 sm:h-12 
        text-center text-base sm:text-lg font-medium
        border rounded
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
        transition-colors
        ${error 
          ? 'border-red-500 bg-red-50' 
          : 'border-gray-400 bg-white hover:border-gray-500'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      aria-label={`PIN digit ${index + 1}`}
    />
  );
};

export default PinInput;